/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState, useEffect } from "react";

/*============================================================
   REDUX DEPENDENCIES
============================================================*/
import { useDispatch, useSelector } from "react-redux";

import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid2";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import Typography from "@mui/material/Typography";

import GridSectionHeader from "../../widgets/GridSectionHeader";
import GridTextfield from "../../widgets/GridTextfield";

import {
  valudateTextfield2,
  validateCurrencyField3,
  removeCommaFromCurrency,
  convertLocaleString,
} from "../../../utils/fieldValidator.js";

import { updateRetirementPlanning } from "../../../states/slcObjectives.js";

import { pvAnnuity, calculateInflatedFutureValue } from "../../../utils/formula.js";

export default function Retirement() {
  const retirement = useSelector((state) => state.objectives.wealthaccumulation.retirementPlanning);

  const [retireAge, setRetirementAge] = useState(retirement.retireAge);
  const [yearsToRetire, setYearsToRetire] = useState(retirement.yearsToRetire);
  const [mthRetireIncToday, setMthRetireIncToday] = useState(retirement.mthRetireIncToday);
  const [annRetireIncToday, setAnnRetireIncToday] = useState(retirement.annRetireIncToday);
  const [pctInfRate, setPctInfRate] = useState(retirement.pctInfRate);

  const [mthRetireIncDesired, setMthRetireIncDesired] = useState(retirement.mthRetireIncDesired);
  const [annRetireIncDesired, setAnnRetireIncDesired] = useState(retirement.annRetireIncDesired);
  const [pctReturnRate, setPctReturnRate] = useState(retirement.pctReturnRate);
  const [pctInfAdjRate, setPctInfAdjRate] = useState(retirement.pctInfAdjRate);
  const [yearsToProvideForRetire, setYearsToProvideForRetire] = useState(retirement.yearsToProvideForRetire);

  const [fundsReqAtRetireAge, setFundsReqAtRetireAge] = useState(retirement.fundsReqAtRetireAge);
  const [liabilityAtRetirement, setLiabilityAtRetirement] = useState(retirement.liabilityAtRetirement);
  const [totalRetireFundNeeded, setTotalRetireFundNeeded] = useState(retirement.totalRetireFundNeeded);

  const [resLifeInsCashValueRoi, setResLifeInsCashValueRoi] = useState(retirement.resLifeInsCashValueRoi);
  const [resLifeInsCashValue, setResLifeInsCashValue] = useState(retirement.resLifeInsCashValue);
  const [resCashAssetsRoi, setResCashAssetsRoi] = useState(retirement.resCashAssetsRoi);
  const [resCashAssets, setResCashAssets] = useState(retirement.resCashAssets);
  const [resInvestmentsRoi, setResInvestmentsRoi] = useState(retirement.resInvestmentsRoi);
  const [resInvestments, setResInvestments] = useState(retirement.resInvestments);
  const [resOtherDesc, setResOtherDesc] = useState(retirement.resOtherDesc);
  const [resOtherDescRoi, setResOtherDescRoi] = useState(retirement.resOtherDescRoi);
  const [resOtherDescAmt, setResOtherDescAmt] = useState(retirement.resOtherDescAmt);
  const [resOtherDescError, setResOtherDescError] = useState(retirement.resOtherDescError);

  const [fundAvailAtRetireAge, setFundAvailAtRetireAge] = useState(retirement.fundAvailAtRetireAge);
  const [surplusShortfall, setSurplusShortfall] = useState(retirement.surplusShortfall);

  const [deviationReason, setDeviationReason] = useState(retirement.deviationReason);
  const [deviationReasonError, setDeviationReasonError] = useState();

  const dispatch = useDispatch();

  //COMPONENT ID
  let id = "";

  /**==============================
   * EFFECTS
   ==============================*/
  useEffect(calcDesiredMonthlyIncome, [mthRetireIncToday, pctInfRate, yearsToRetire]);
  useEffect(calcFundAtRetirementAge, [annRetireIncDesired, pctInfAdjRate, yearsToProvideForRetire]);
  useEffect(calcTotalRetirementFundNeeded, [fundsReqAtRetireAge, liabilityAtRetirement]);

  useEffect(calcFundsAvailAtRetirementAge, [resLifeInsCashValue, resCashAssets, resInvestments, resOtherDescAmt]);
  useEffect(calcSurplusOrShortfall, [fundAvailAtRetireAge, totalRetireFundNeeded]);

  /**==============================
   * FUNCTIONS
   ==============================*/
  function calcDesiredMonthlyIncome() {
    //CALCULATE FV (Desired Monthly Income)
    let futureValue = calculateInflatedFutureValue(
      Number(removeCommaFromCurrency(mthRetireIncToday)),
      Number(pctInfRate),
      Number(yearsToRetire)
    );

    //CALCULATE FV (Desired Annual Income)
    let annualFutureValue = futureValue * 12;

    //CONVERT INTO COMMA CURRENCY STRING WITH COMMA
    annualFutureValue = convertLocaleString(annualFutureValue, "en-US", 2);
    futureValue = convertLocaleString(futureValue, "en-US", 2);

    updateRedux("mthRetireIncDesired", futureValue, updateRetirementPlanning);
    updateRedux("annRetireIncDesired", annualFutureValue, updateRetirementPlanning);
    setMthRetireIncDesired(futureValue);
    setAnnRetireIncDesired(annualFutureValue);
  }

  function calcFundAtRetirementAge() {
    //CALCULATE FUNDS REQUIRED AT RETIREMENT AGE (PV)
    let presentValue = pvAnnuity(
      Number(removeCommaFromCurrency(annRetireIncDesired)),
      Number(pctInfAdjRate) / 100,
      Number(yearsToProvideForRetire),
      1
    );

    presentValue = convertLocaleString(presentValue, "en-US", 2);
    updateRedux("fundsReqAtRetireAge", presentValue, updateRetirementPlanning);
    setFundsReqAtRetireAge(presentValue);
  }

  function calcTotalRetirementFundNeeded() {
    let totalRetireFundNeeded =
      Number(removeCommaFromCurrency(fundsReqAtRetireAge)) + Number(removeCommaFromCurrency(liabilityAtRetirement));

    totalRetireFundNeeded = convertLocaleString(totalRetireFundNeeded, "en-US", 2);
    updateRedux("totalRetireFundNeeded", totalRetireFundNeeded, updateRetirementPlanning);
    setTotalRetireFundNeeded(totalRetireFundNeeded);
  }

  function calcFundsAvailAtRetirementAge() {
    let totalFundAtRetirementAge =
      Number(removeCommaFromCurrency(resLifeInsCashValue)) +
      Number(removeCommaFromCurrency(resCashAssets)) +
      Number(removeCommaFromCurrency(resInvestments)) +
      Number(removeCommaFromCurrency(resOtherDescAmt));

    totalFundAtRetirementAge = convertLocaleString(totalFundAtRetirementAge, "en-US", 2);
    updateRedux("fundAvailAtRetireAge", totalFundAtRetirementAge, updateRetirementPlanning);
    setFundAvailAtRetireAge(totalFundAtRetirementAge);
  }

  function calcSurplusOrShortfall() {
    let surplusShortfall =
      Number(removeCommaFromCurrency(fundAvailAtRetireAge)) - Number(removeCommaFromCurrency(totalRetireFundNeeded));

    surplusShortfall = convertLocaleString(surplusShortfall, "en-US", 2);

    updateRedux("surplusShortfall", surplusShortfall, updateRetirementPlanning);
    setSurplusShortfall(surplusShortfall);
  }

  function updateRedux(fieldName, fieldValue, reducer) {
    const action = {
      type: fieldName,
      payload: fieldValue,
    };
    dispatch(reducer(action));
  }

  function updateRedux(fieldName, fieldValue, reducer) {
    const action = {
      type: fieldName,
      payload: fieldValue,
    };
    dispatch(reducer(action));
  }

  function handleChange(e, object) {
    let input_data = {
      event: e,
      fieldName: "",
      fieldVariable: "",
      isMandatory: false,
    };

    //TEXTFIELDS
    id = e.target.id;
    validateFields(e, id, input_data);
  }

  function validateFields(e, id, input_data) {
    /**==============================
     * RETIREMENT PLAN
     ==============================*/
    if (id === "retireAge") {
      input_data.fieldName = "Desired Retirement Age";
      input_data.fieldVariable = retireAge;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setRetirementAge(result.value);
        updateRedux("retireAge", result.value, updateRetirementPlanning);
      }
    }

    if (id === "yearsToRetire") {
      input_data.fieldName = "Years to Retirement";
      input_data.fieldVariable = yearsToRetire;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setYearsToRetire(result.value);
        updateRedux("yearsToRetire", result.value, updateRetirementPlanning);
      }
    }

    if (id === "mthRetireIncToday") {
      input_data.fieldName = "Desired Monthly Retirement Income (Today's Value)";
      input_data.fieldVariable = mthRetireIncToday;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        updateRedux("mthRetireIncToday", result.value, updateRetirementPlanning);
        updateRedux("annRetireIncToday", result.value, updateRetirementPlanning);
        setMthRetireIncToday(result.value);

        //Calculate annual amount
        let annual = Number(removeCommaFromCurrency(result.value)) * 12;
        setAnnRetireIncToday(convertLocaleString(annual, "en-US", 2));
      }
    }

    if (id === "pctInfRate") {
      input_data.fieldName = "Assumed Inflation Rate";
      input_data.fieldVariable = pctInfRate;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setPctInfRate(result.value);
        updateRedux("pctInfRate", result.value, updateRetirementPlanning);
      }
    }

    if (id === "pctReturnRate") {
      input_data.fieldName = "Assumed Inflation Rate";
      input_data.fieldVariable = pctReturnRate;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setPctReturnRate(result.value);
        updateRedux("pctReturnRate", result.value, updateRetirementPlanning);
      }
    }

    if (id === "pctInfAdjRate") {
      input_data.fieldName = "Inflation Adjusted Investment Rate";
      input_data.fieldVariable = pctInfAdjRate;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setPctInfAdjRate(result.value);
        updateRedux("pctInfAdjRate", result.value, updateRetirementPlanning);
      }
    }

    if (id === "yearsToProvideForRetire") {
      input_data.fieldName = "Years To Provide For Retirement";
      input_data.fieldVariable = yearsToProvideForRetire;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setYearsToProvideForRetire(result.value);
        updateRedux("yearsToProvideForRetire", result.value, updateRetirementPlanning);
      }
    }

    if (id === "liabilityAtRetirement") {
      input_data.fieldName = "Remaining Liabilities At Retirement";
      input_data.fieldVariable = liabilityAtRetirement;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setLiabilityAtRetirement(result.value);
        updateRedux("liabilityAtRetirement", result.value, updateRetirementPlanning);
      }
    }

    if (id === "deviationReason") {
      input_data.fieldName = "Deviation reason";
      input_data.fieldLength = 1000;
      input_data.fieldVariable = deviationReason;
      const result = valudateTextfield2(input_data);
      if (result.errorMessage === "") {
        setDeviationReason(result.value);
        updateRedux("deviationReason", result.value, updateRetirementPlanning);
      }
      setDeviationReasonError(result.errorMessage);
    }

    /**==============================
     * EXISTING RESOURCES ALLOCATED
     ==============================*/
    if (id === "resLifeInsCashValueRoi") {
      input_data.fieldName = "ROI(%)";
      input_data.fieldVariable = resLifeInsCashValueRoi;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResLifeInsCashValueRoi(result.value);
        updateRedux("resLifeInsCashValueRoi", result.value, updateRetirementPlanning);
      }
    }

    if (id === "resLifeInsCashValue") {
      input_data.fieldName = "Life Insurance Cash Value";
      input_data.fieldVariable = resLifeInsCashValue;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        console.log("B = " + result.value);
        setResLifeInsCashValue(result.value);
        updateRedux("resLifeInsCashValue", result.value, updateRetirementPlanning);
      }
    }

    if (id === "resCashAssetsRoi") {
      input_data.fieldName = "ROI(%)";
      input_data.fieldVariable = resCashAssetsRoi;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResCashAssetsRoi(result.value);
        updateRedux("resCashAssetsRoi", result.value, updateRetirementPlanning);
      }
    }

    if (id === "resCashAssets") {
      input_data.fieldName = "Cash Assets";
      input_data.fieldVariable = resCashAssets;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResCashAssets(result.value);
        updateRedux("resCashAssets", result.value, updateRetirementPlanning);
      }
    }

    if (id === "resInvestmentsRoi") {
      input_data.fieldName = "ROI(%)";
      input_data.fieldVariable = resInvestmentsRoi;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResInvestmentsRoi(result.value);
        updateRedux("resInvestmentsRoi", result.value, updateRetirementPlanning);
      }
    }

    if (id === "resInvestments") {
      input_data.fieldName = "Investments";
      input_data.fieldVariable = resInvestments;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResInvestments(result.value);
        updateRedux("resInvestments", result.value, updateRetirementPlanning);
      }
    }

    if (id === "resOtherDesc") {
      input_data.fieldName = "Others description";
      input_data.fieldVariable = resOtherDesc;
      const result = valudateTextfield2(input_data);
      if (result.errorMessage === "") {
        setResOtherDesc(result.value);
        updateRedux("resOtherDesc", result.value, updateRetirementPlanning);
      }

      setResOtherDescError(result.errorMessage);
    }

    if (id === "resOtherDescRoi") {
      input_data.fieldName = "ROI(%)";
      input_data.fieldVariable = resOtherDescRoi;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResOtherDescRoi(result.value);
        updateRedux("resOtherDescRoi", result.value, updateRetirementPlanning);
      }
    }

    if (id === "resOtherDescAmt") {
      input_data.fieldName = "Other Resource Amount";
      input_data.fieldVariable = resOtherDescAmt;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResOtherDescAmt(result.value);
        updateRedux("resOtherDescAmt", result.value, updateRetirementPlanning);
      }
    }
  }

  return (
    <>
      <Grid container spacing={2} size={12}>
        <GridSectionHeader
          sectionName="Planning for Retirement"
          sx={{
            p: 1,
            pl: 1.8,
            mt: 0,
            mb: 1,
            fontSize: 18,
            fontWeight: "bold",
            color: "#FFFFFF", //WHITE
            backgroundColor: "#10B981",
          }}
        />

        {/*==============================
          RETIREMENT AGE
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            Desired Retirement Age
          </Typography>
        </Grid>

        <GridTextfield
          id="retireAge"
          key="retireAge"
          columnSize="3"
          currState={retireAge}
          onChange={handleChange}
          disabled={false}
        />

        {/*==============================
        YEARS TO RETIREMENT
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            Number of Years to Retirement(n)
          </Typography>
        </Grid>

        <GridTextfield
          id="yearsToRetire"
          key="yearsToRetire"
          columnSize="3"
          currState={yearsToRetire}
          onChange={handleChange}
          disabled={false}
        />

        {/*==============================
        TODAY'S VALUE - DESIRED MONTHLY RETIREMENT INCOME
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            Desired Monthly Retirement Income in Today's Value (pv)
          </Typography>
        </Grid>
        <GridTextfield
          id="mthRetireIncToday"
          key="mthRetireIncToday"
          columnSize="3"
          isCurrency={true}
          label="Monthly"
          currState={mthRetireIncToday}
          onChange={handleChange}
          disabled={false}
        />

        {/*==============================
        TODAY'S VALUE - DESIRED ANNUAL RETIREMENT INCOME
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            Desired Annual Retirement Income in Today's Value (pv)
          </Typography>
        </Grid>
        <GridTextfield
          id="annRetireIncToday"
          key="annRetireIncToday"
          columnSize="3"
          isCurrency={true}
          label="Annually"
          currState={annRetireIncToday}
          onChange={handleChange}
          disabled={true}
        />
      </Grid>

      {/*==============================
        ASSUMED INFLATION RATE
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 0.5,
          }}
        >
          Assumed Inflation Rate. (%) (i)
        </Typography>
        <Typography variant="body1">(Input between 0% - 9%. If deviate, Representative to justify)</Typography>
      </Grid>

      <GridTextfield
        id="pctInfRate"
        key="pctInfRate"
        columnSize="3"
        label="Percent"
        currState={pctInfRate}
        onChange={handleChange}
      />

      {/*==============================
        DESIRED MONTHLY RETIREMENT INCOME
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Desired Monthly Retirement Income (fv)
        </Typography>
      </Grid>
      <GridTextfield
        id="mthRetireIncDesired"
        key="mthRetireIncDesired"
        columnSize="3"
        label="Monthly"
        isCurrency={true}
        currState={mthRetireIncDesired}
        onChange={handleChange}
        disabled={true}
      />

      {/*==============================
        DESIRED ANNUAL RETIREMENT INCOME
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Desired Annual Retirement Income (fv)
        </Typography>
      </Grid>
      <GridTextfield
        id="annRetireIncDesired"
        key="annRetireIncDesired"
        columnSize="3"
        label="Annually"
        isCurrency={true}
        currState={annRetireIncDesired}
        onChange={handleChange}
        disabled={true}
      />

      {/*==============================
        INVESTMENT RATE OF RETURN
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 0.5,
          }}
        >
          Investment Rate of Return (%) (R) *
        </Typography>
        <Typography variant="body1">(Input between 0% - 9%. If deviate, Representative to justify)</Typography>
      </Grid>
      <GridTextfield
        id="pctReturnRate"
        key="pctReturnRate"
        columnSize="3"
        label="Percent"
        currState={pctReturnRate}
        onChange={handleChange}
        disabled={false}
      />

      {/*==============================
        INFLATION ADJUSTMENT INVESTMENT RATE AFTER RETIREMENT
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Inflation Adjustment Investment Rate After Retirement, (%) (i)
        </Typography>
      </Grid>
      <GridTextfield
        id="pctInfAdjRate"
        key="pctInfAdjRate"
        columnSize="3"
        label="Percent"
        currState={pctInfAdjRate}
        onChange={handleChange}
        disabled={false}
      />

      {/*==============================
        INFLATION ADJUSTMENT INVESTMENT RATE AFTER RETIREMENT
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Number of Years to Provide for Retirement (n1)^
        </Typography>
      </Grid>
      <GridTextfield
        id="yearsToProvideForRetire"
        key="yearsToProvideForRetire"
        columnSize="3"
        currState={yearsToProvideForRetire}
        onChange={handleChange}
        disabled={false}
      />

      {/*==============================
        FUND REQUIRED AT RETIREMENT AGE
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
            fontWeight: "bold",
          }}
        >
          (A) Funds Required at Retirement Age
        </Typography>
      </Grid>
      <GridTextfield
        id="fundsReqAtRetireAge"
        key="fundsReqAtRetireAge"
        columnSize="3"
        isCurrency={true}
        currState={fundsReqAtRetireAge}
        onChange={handleChange}
        disabled={true}
      />

      {/*==============================
        REMAINING LIABILITIES AT RETIREMENT (IF ANY)
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
            fontWeight: "bold",
          }}
        >
          (B) Remaining Liabilities at Retirement (if any)
        </Typography>
      </Grid>
      <GridTextfield
        id="liabilityAtRetirement"
        key="liabilityAtRetirement"
        columnSize="3"
        isCurrency={true}
        currState={liabilityAtRetirement}
        onChange={handleChange}
        disabled={false}
      />

      {/*==============================
        REMAINING LIABILITIES AT RETIREMENT (IF ANY)
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
            fontWeight: "bold",
          }}
        >
          (C) Total Retirement Fund Needed = (A) + (B)
        </Typography>
      </Grid>
      <GridTextfield
        id="totalRetireFundNeeded"
        key="totalRetireFundNeeded"
        columnSize="3"
        isCurrency={true}
        currState={totalRetireFundNeeded}
        onChange={handleChange}
        disabled={true}
      />

      {/*==============================
        EXISTING RESOURCES ALLOCATED FOR RETIREMENT
      ==============================*/}
      <Grid size={12}>
        <Typography
          variant="h5"
          sx={{
            pt: 0.5,
            fontWeight: "bold",
            textDecoration: "underline",
          }}
        >
          Existing Resources Allocated for Retirement (Projected Values)
        </Typography>
      </Grid>

      {/*==============================
        LIFE INSURANCE CASH VALUES
      ==============================*/}
      <Grid size={6}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Life insurance Cash Value
        </Typography>
      </Grid>

      <GridTextfield
        id="resLifeInsCashValueRoi"
        key="resLifeInsCashValueRoi"
        columnSize="3"
        label="ROI (%)"
        currState={resLifeInsCashValueRoi}
        onChange={handleChange}
        disabled={false}
      />

      <GridTextfield
        id="resLifeInsCashValue"
        key="resLifeInsCashValue"
        columnSize="3"
        isCurrency={true}
        currState={resLifeInsCashValue}
        onChange={handleChange}
        disabled={false}
      />

      {/*==============================
        CASH ASSET
      ==============================*/}
      <Grid size={6}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Cash Assets (Savings, Fixed Deposits, etc.) #
        </Typography>
      </Grid>

      <GridTextfield
        id="resCashAssetsRoi"
        key="resCashAssetsRoi"
        columnSize="3"
        label="ROI (%)"
        currState={resCashAssetsRoi}
        onChange={handleChange}
        disabled={false}
      />

      <GridTextfield
        id="resCashAssets"
        key="resCashAssets"
        columnSize="3"
        isCurrency={true}
        currState={resCashAssets}
        onChange={handleChange}
        disabled={false}
      />

      {/*==============================
        INVESTMENTS (BONDS, UNIT TRUSTS, SHARES, ETC...)
      ==============================*/}
      <Grid size={6}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Investments (Bonds, Unit Trusts, Shares, etc.) #
        </Typography>
      </Grid>

      <GridTextfield
        id="resInvestmentsRoi"
        key="resInvestmentsRoi"
        columnSize="3"
        label="ROI (%)"
        currState={resInvestmentsRoi}
        onChange={handleChange}
        disabled={false}
      />

      <GridTextfield
        id="resInvestments"
        key="resInvestments"
        columnSize="3"
        isCurrency={true}
        currState={resInvestments}
        onChange={handleChange}
        disabled={false}
      />

      {/*==============================
        OTHERS
      ==============================*/}
      <Grid size={1}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Others:
        </Typography>
      </Grid>

      <GridTextfield
        id="resOtherDesc"
        key="resOtherDesc"
        columnSize="5"
        currState={resOtherDesc}
        onChange={handleChange}
        error={resOtherDescError}
        helperText={resOtherDescError}
      />

      <GridTextfield
        id="resOtherDescRoi"
        key="resOtherDescRoi"
        columnSize="3"
        label="ROI (%)"
        currState={resOtherDescRoi}
        onChange={handleChange}
        disabled={false}
      />

      <GridTextfield
        id="resOtherDescAmt"
        key="resOtherDescAmt"
        columnSize="3"
        isCurrency={true}
        currState={resOtherDescAmt}
        onChange={handleChange}
        disabled={false}
      />

      {/*==============================
        FUNDS AVAILABLE AT RETIREMENT AGE (fv)
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          <b>(D) Funds Available at Retirement Age (fv)</b>
        </Typography>
      </Grid>

      <GridTextfield
        id="fundAvailAtRetireAge"
        key="fundAvailAtRetireAge"
        columnSize="3"
        isCurrency={true}
        currState={fundAvailAtRetireAge}
        onChange={handleChange}
        disabled={true}
      />

      {/*==============================
        SURPLUS / SHORTFALL
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
            fontWeight: "bold",
          }}
        >
          (D) - (C) = Surplus / (Shortfall) at Retirement
        </Typography>
      </Grid>

      <GridTextfield
        id="surplusShortfall"
        key="surplusShortfall"
        columnSize="3"
        isCurrency={true}
        currState={surplusShortfall}
        onChange={handleChange}
        disabled={true}
      />

      <Typography variant="body1">Reason(s) for Deviations:</Typography>
      <TextareaAutosize
        id="deviationReason"
        placeholder="Type here..."
        style={{ width: "100%" }}
        minRows={5}
        onChange={handleChange}
        defaultValue={deviationReason}
      />
      {deviationReasonError && <FormHelperText error={deviationReasonError}>{deviationReasonError}</FormHelperText>}

      <Typography variant="body2">
        ^ ‘Number of Years to Provide for Retirement (n1)’ should base on life expectancy of client less statutory
        retirement age. If deviate, Representative to justify. Life expectancy for male is 82 and female is 86.
        Statutory retirement age is 63.
      </Typography>

      <Typography variant="body2">
        # Summation of Cash Assets allocated in needs analysis for retirement, children’s education and/or savings for
        other purposes should not exceed 100% of Total Cash/Near Cash declared in Section 6. If deviate, Representative
        to justify. Similarly for Investments, summation of Investments allocated in needs analysis for retirement,
        children’s education and/or savings for other purposes should not exceed 100% of Unit Trust + Stocks + Bonds
        declared in Section 6. If deviate, Representative to justify.
      </Typography>
    </>
  );
}
